import * as L from 'leaflet';
import { environment } from 'src/environments/environment';
import { OPERATORS } from 'src/app/services/constants';
import { CollectionPopupComponent } from '../../shared';
import { WeatherExHost } from './weatherex-map.types';

// ─── Marker style constants ──────────────────────────────────────────────────

const MARKER_BASE: L.CircleMarkerOptions = {
  radius: 4,
  weight: 1,
  color: '#1976d2',
  fillColor: '#42a5f5',
  fillOpacity: 0.85,
};

const MARKER_HOVER: L.CircleMarkerOptions = {
  radius: 6,
  weight: 2,
  color: '#0d47a1',
  fillColor: '#1e88e5',
  fillOpacity: 1,
};

/**
 * Owns all WeatherEx map state and logic, extracted from
 * LeafletNewcollectionsComponent to keep the parent lean.
 *
 * Lifecycle: instantiated once per component instance via
 *   `readonly weatherEx = new WeatherExMapController(this);`
 *
 * The host interface (WeatherExHost) deliberately exposes only what this
 * controller actually uses — no broad component reference.
 */
export class WeatherExMapController {

  // ── POI / GeoJSON marker layer ───────────────────────────────────────────
  active = false;
  isPoi = false;
  private layer: L.LayerGroup | null = null;
  private canvasRenderer = L.canvas({ padding: 0.5 });
  /** Exposed for the component's opacity slider (chngSceneOpacity). */
  markers: Array<{ marker: any; feature: any }> = [];
  rendering = false;

  // ── Shared time / variable state ─────────────────────────────────────────
  times: string[] = [];
  variables: string[] = [];
  units: { [key: string]: string } = {};
  dates: string[] = [];
  private timesByDate: { [date: string]: Array<{ iso: string; index: number; label: string }> } = {};
  selectedDate: string | null = null;
  selectedTimeIndex = 0;
  collectionMode: { key: string; name: string; description?: string }[] = [];

  // ── WMS-T (AOI / GeoServer) state ────────────────────────────────────────
  wmsLayers: Map<string, L.TileLayer> = new Map();
  activeVariables: Set<string> = new Set();
  activeVariablesList: string[] = [];
  wmsWorkspace = '';
  wmsPointValues: { [variable: string]: number | null } = {};
  private boundsLayer: L.Layer | null = null;
  private targetGeoJson: any = null;

  // ── Point tooltip / drawer state ─────────────────────────────────────────
  pointLoading = false;
  activeFeature: any = null;
  tooltipVisible = false;
  tooltipPixelPos: { x: number; y: number } | null = null;
  tooltipPlacement = 'above-right';
  tooltipMaxHeight = 0;

  // ── Internal WMS interaction state ───────────────────────────────────────
  private wmsClickLatLng: L.LatLng | null = null;
  private clickMarker: L.CircleMarker | null = null;
  private suppressNextMapClick = false;

  // Stable bound references so map.on/off always target the same function.
  private readonly onMapClickBound = (e: L.LeafletMouseEvent): void => this.onMapClick(e);
  private readonly onClickMarkerHoverBound = (e: L.LeafletMouseEvent): void => this.onClickMarkerHover(e);

  constructor(private readonly host: WeatherExHost) {}

  // ── Public slider / date event handlers (bound in template) ─────────────

  onTimeChange(index: number): void {
    this.applyTimeIndex(index);
  }

  onSelectDate(dateStr: string): void {
    if (!dateStr || !this.timesByDate[dateStr]) return;
    this.selectedDate = dateStr;
    const firstSlot = this.timesByDate[dateStr][0];
    this.applyTimeIndex(firstSlot ? firstSlot.index : 0);
  }

  onSliderChange(position: number | null): void {
    if (position == null) return;
    const slot = this.timeSlots[position];
    if (slot) this.onTimeChange(slot.index);
  }

  // ── Getters (template bindings) ──────────────────────────────────────────

  get timeSlots(): Array<{ iso: string; index: number; label: string }> {
    if (!this.selectedDate) return [];
    return this.timesByDate[this.selectedDate] ?? [];
  }

  get sliderPosition(): number {
    const slots = this.timeSlots;
    if (!slots.length) return 0;
    const pos = slots.findIndex(s => s.index === this.selectedTimeIndex);
    return pos >= 0 ? pos : 0;
  }

  // ── Fresh-data fetch ─────────────────────────────────────────────────────

  fetchData(collection: any, sensorIndex: number): void {
    const sensor = collection.sensors[sensorIndex];
    const sensorTaskId = sensor?.id;
    if (!sensorTaskId) {
      this.host.spinnerService.hide();
      return;
    }

    const toiStart = collection?.openWindow ? new Date(collection.openWindow) : null;
    const toiEnd   = collection?.closeWindow ? new Date(collection.closeWindow) : null;
    const now = new Date();

    if (toiStart && now < toiStart) {
      this.host.popupService.openPopup(CollectionPopupComponent,
        'WeatherEx forecast data is not available until the start of the time of interest (TOI).', '', '40%');
      this.uncheckSensor(sensor);
      this.host.spinnerService.hide();
      return;
    }
    if (toiEnd && now > toiEnd) {
      this.host.popupService.openPopup(CollectionPopupComponent,
        'The time of interest (TOI) has ended. WeatherEx forecast data is no longer available. Please re-task if you wish to continue monitoring this area.', '', '40%');
      this.uncheckSensor(sensor);
      this.host.spinnerService.hide();
      return;
    }
    if (sensor?.noOfRefreshes != null) {
      const remaining = sensor.updatedRefreshes == 0 ? 0 : (sensor.updatedRefreshes || sensor.noOfRefreshes);
      if (remaining <= 0) {
        this.host.popupService.openPopup(CollectionPopupComponent,
          'You have no remaining WeatherEx refreshes. Please re-task if you wish to fetch new forecast data.', '', '40%');
        this.uncheckSensor(sensor);
        this.host.spinnerService.hide();
        return;
      }
    }

    // Parse target polygon from collection data
    try {
      this.targetGeoJson = typeof collection.target === 'string'
        ? JSON.parse(collection.target)
        : (collection.target ?? null);
    } catch (_) {
      this.targetGeoJson = null;
    }

    this.resetForecastSelection(sensor);
    this.ensureBoundsLayerOnMap();
    this.host.spinnerService.show();
    this.host.mapLoad = true;

    this.host.apiService.getWeatherExData({ taskid: sensorTaskId }).subscribe({
      next: (res: any) => {
        this.host.spinnerService.hide();
        this.host.mapLoad = false;
        if (res?.status === 'failed' || res?.msg === 'Failed') {
          const message = res?.message || 'An error occurred fetching WeatherEx data. Please try again.';
          this.host.popupService.openPopup(CollectionPopupComponent, message, '', '40%');
          sensor.checked = false;
          return;
        }
        const refreshEntry = res?.newRefreshHistory;
        if (!refreshEntry) {
          this.host.spinnerService.hide();
          this.host.mapLoad = false;
          sensor.checked = false;
          return;
        }

        if (!sensor.refreshHistory) sensor.refreshHistory = [];
        sensor.refreshHistory.push(refreshEntry);

        if (sensor.updatedRefreshes) sensor.updatedRefreshes -= 1;
        else sensor.updatedRefreshes = (sensor.noOfRefreshes ?? 1) - 1;

        const onDone = () => { this.host.spinnerService.hide(); this.host.mapLoad = false; };
        if (refreshEntry.geoserverWorkspace) {
          this.loadWmsLayers(refreshEntry.geoserverWorkspace, [], refreshEntry.refreshId, onDone, sensor.collectionMode);
        } else if (refreshEntry.s3?.signedUrl) {
          this.host.spinnerService.show();
          this.host.mapLoad = true;
          this.collectionMode = this.sortedCollectionMode(sensor.collectionMode);
          this.host.httpClient.get(refreshEntry.s3.signedUrl).subscribe({
            next: (geoJson: any) => {
              this.renderTimeseries(this.normalizeForRendering(geoJson), refreshEntry.refreshId, onDone);
            },
            error: () => { onDone(); sensor.checked = false; },
          });
        } else {
          onDone();
          sensor.checked = false;
        }
      },
      error: () => {
        this.host.spinnerService.hide();
        this.host.mapLoad = false;
        sensor.checked = false;
      },
    });
  }

  // ── POI GeoJSON rendering ────────────────────────────────────────────────

  renderTimeseries(geoJson: any, refreshId?: any, onDone?: () => void): void {
    this.clearLayer(true);
    this.isPoi = true;

    this.times     = Array.isArray(geoJson?.times) ? geoJson.times : [];
    this.variables = this.sortedVarKeys(Array.isArray(geoJson?.variables) ? geoJson.variables : []);
    this.units     = geoJson?.units && typeof geoJson.units === 'object' ? geoJson.units : {};

    this.buildDateIndex();
    this.markers = [];
    this.rendering = true;
    this.host.changDetectionRef.detectChanges();

    setTimeout(() => {
      const layerGroup = L.geoJSON(geoJson, {
        pointToLayer: (_feature, latlng) =>
          L.circleMarker(latlng, { ...MARKER_BASE, renderer: this.canvasRenderer }),
        onEachFeature: (feature, layer) => {
          layer.on('mouseover', () => this.onPoiMarkerMouseover(feature, layer));
          layer.on('mouseout',  () => this.onPoiMarkerMouseout(layer));
          this.markers.push({ marker: layer, feature });
        },
      }) as any;

      layerGroup.refreshId = refreshId;
      layerGroup.addTo(this.host.map);
      this.layer = layerGroup;
      this.active = this.markers.length > 0;

      this.ensureBoundsLayerOnMap();
      this.rendering = false;

      if (this.dates.length > 0) {
        this.selectedDate = this.dates[0];
        const firstSlot = this.timesByDate[this.dates[0]]?.[0];
        this.selectedTimeIndex = firstSlot ? firstSlot.index : 0;
      } else {
        this.selectedDate = null;
        this.selectedTimeIndex = 0;
      }

      try {
        const bounds = (layerGroup as L.FeatureGroup).getBounds?.();
        if (bounds?.isValid()) {
          if (this.markers.length === 1) {
            this.host.map.setView(bounds.getCenter(), Math.max(this.host.map.getZoom(), 10));
          } else {
            this.host.map.fitBounds(bounds, { padding: [50, 50] });
          }
        }
      } catch (_) {}

      this.host.changDetectionRef.detectChanges();
      onDone?.();
    }, 0);
  }

  // ── WMS-T (AOI / GeoServer) ──────────────────────────────────────────────

  loadWmsLayers(
    workspace: string,
    times: string[],
    _refreshId?: any,
    onDone?: () => void,
    collectionMode?: { key: string; name: string; description?: string }[],
  ): void {
    this.clearLayer(true);
    this.isPoi = false;
    this.wmsWorkspace   = workspace;
    this.collectionMode = this.sortedCollectionMode(collectionMode);

    this.host.apiService.getWeatherExNcMeta(workspace).subscribe({
      next: (meta) => {
        this.times     = meta.times?.length ? meta.times : times;
        const cmKeys   = this.collectionMode.map(cm => cm.key);
        const rawVars  = cmKeys.length ? cmKeys : (meta.variables ?? []);
        this.variables = this.sortedVarKeys(rawVars);
        this.units     = meta.units ?? {};
        this.buildDateIndex();

        if (this.dates.length > 0) {
          this.selectedDate       = this.dates[0];
          const firstSlot         = this.timesByDate[this.dates[0]]?.[0];
          this.selectedTimeIndex  = firstSlot ? firstSlot.index : 0;
        }

        if (this.targetGeoJson) {
          this.ensureBoundsLayerOnMap();
          try {
            const lb = (this.boundsLayer as L.GeoJSON)?.getBounds?.();
            if (lb?.isValid()) this.host.map.fitBounds(lb, { padding: [50, 50] });
          } catch (_) {}
        } else if (meta.bounds?.length === 2) {
          // Fit map to meta bounds when no target polygon is present.
          try {
            const lb = L.latLngBounds(meta.bounds as any);
            if (lb.isValid()) this.host.map.fitBounds(lb, { padding: [50, 50] });
          } catch (_) {}
        }

        const defaultIso = this.times[0] ?? '';
        const firstVar   = this.variables[0];
        if (firstVar) {
          this.activeVariables.add(firstVar);
          const wmsLayer = this.createWmsLayer(workspace, firstVar, defaultIso);
          wmsLayer.addTo(this.host.map);
          this.wmsLayers.set(firstVar, wmsLayer);
        }
        this.activeVariablesList = Array.from(this.activeVariables);
        this.active = this.variables.length > 0;

        this.host.map.off('click', this.onMapClickBound);
        this.host.map.on('click', this.onMapClickBound);

        this.host.changDetectionRef.detectChanges();
        onDone?.();
      },
      error: (err) => {
        console.warn('[WeatherEx] NC meta not available:', err?.error?.error);
        onDone?.();
      },
    });
  }

  toggleVariable(variable: string): void {
    const alreadyActive = this.activeVariables.has(variable);
    if (alreadyActive) {
      this.activeVariables.delete(variable);
      this.wmsLayers.get(variable)?.remove();
    } else {
      this.activeVariables.add(variable);
      const existing = this.wmsLayers.get(variable);
      if (existing) {
        existing.addTo(this.host.map);
      } else {
        const iso = this.times[this.selectedTimeIndex] ?? '';
        try {
          const wmsLayer = this.createWmsLayer(this.wmsWorkspace, variable, iso);
          wmsLayer.on('tileerror', (e: any) => console.error(`[WeatherEx] Tile error "${variable}":`, e.error ?? e));
          wmsLayer.addTo(this.host.map);
          this.wmsLayers.set(variable, wmsLayer);
        } catch (err) {
          console.error(`[WeatherEx] createWmsLayer threw for "${variable}":`, err);
        }
      }
    }
    this.activeVariablesList = Array.from(this.activeVariables);
    this.host.changDetectionRef.detectChanges();
  }

  // ── Tooltip / drawer public handlers ─────────────────────────────────────

  closeDrawer(): void {
    this.activeFeature    = null;
    this.wmsClickLatLng   = null;
    this.tooltipVisible   = false;
    this.tooltipPixelPos  = null;
    this.tooltipMaxHeight = 0;
    this.suppressNextMapClick = true;
  }

  closeTooltip(): void {
    this.tooltipVisible   = false;
    this.tooltipPixelPos  = null;
    this.tooltipPlacement = 'above-right';
    this.tooltipMaxHeight = 0;
    this.activeFeature   = null;
    this.pointLoading    = false;
    if (this.clickMarker) {
      try { this.clickMarker.remove(); } catch (_) {}
      this.clickMarker = null;
    }
    this.wmsClickLatLng = null;
    this.suppressNextMapClick = true;
    this.host.changDetectionRef.detectChanges();
  }

  onTooltipLeave(): void {
    this.tooltipVisible = false;
    this.host.changDetectionRef.detectChanges();
  }

  // ── Layer / state cleanup ────────────────────────────────────────────────

  clearLayer(keepBoundsLayer = false): void {
    this.clearWmsLayers();

    if (!keepBoundsLayer) {
      if (this.boundsLayer) {
        try { this.boundsLayer.remove(); } catch (_) {}
        this.boundsLayer = null;
      }
      this.targetGeoJson = null;
    }
    if (this.layer) {
      try { this.host.map.removeLayer(this.layer); } catch (_) {}
    }
    this.layer               = null;
    this.markers             = [];
    this.times               = [];
    this.variables           = [];
    this.collectionMode      = [];
    this.units               = {};
    this.dates               = [];
    this.timesByDate         = {};
    this.selectedDate        = null;
    this.selectedTimeIndex   = 0;
    this.active              = false;
    this.activeFeature       = null;
    this.tooltipVisible      = false;
    this.tooltipPixelPos     = null;
    this.tooltipPlacement    = 'above-right';
    this.tooltipMaxHeight    = 0;
    this.isPoi               = false;
  }

  /** Clear WeatherEx state AND uncheck all WeatherEx sensor/history selections. */
  resetForecastSelection(activeSensor?: any, activeRefreshId?: any): void {
    // Keep the target polygon when resetting for a refresh/replay (activeSensor or activeRefreshId
    // present) — it should only be removed on a full task switch (no-args call from clickTask).
    this.clearLayer(!!activeSensor || !!activeRefreshId);
    const collections = Array.isArray(this.host.collections) ? this.host.collections : [];
    for (const collection of collections) {
      const sensors = Array.isArray(collection?.sensors) ? collection.sensors : [];
      for (const sensor of sensors) {
        if (sensor?.operatorKey !== OPERATORS.WEATHEREX) continue;
        sensor.checked = activeSensor && sensor?.id === activeSensor?.id;
        if (Array.isArray(sensor.refreshHistory)) {
          sensor.refreshHistory.forEach((h: any) => {
            h.checked = activeRefreshId && h.refreshId === activeRefreshId;
          });
        }
      }
    }
  }

  /** Draw the collection's target polygon immediately (e.g. on task load, before forecast is fetched). */
  showTarget(collection: any): void {
    try {
      const rawTarget = collection?.target;
      if (!rawTarget) return;
      const geoJson = typeof rawTarget === 'string' ? JSON.parse(rawTarget) : rawTarget;
      this.targetGeoJson = geoJson;
      if (this.boundsLayer) {
        try { this.boundsLayer.remove(); } catch (_) {}
        this.boundsLayer = null;
      }
      this.boundsLayer = L.geoJSON(geoJson, {
        style: () => ({
          color: '#1976d2',
          fillColor: 'transparent',
          weight: 2,
          dashArray: '6 4',
        }),
        interactive: false,
      }).addTo(this.host.map);
      const bounds = (this.boundsLayer as L.GeoJSON).getBounds();
      if (bounds?.isValid()) this.host.map.fitBounds(bounds, { padding: [50, 50] });
    } catch (_) {}
  }

  /**
   * Returns the bounding box of the target polygon, or null if none is set.
   * Also ensures the bounds layer is re-attached to the map.
   */
  getTargetBounds(): L.LatLngBounds | null {
    if (!this.targetGeoJson) return null;
    try {
      this.ensureBoundsLayerOnMap();
      const bounds = (this.boundsLayer as L.GeoJSON)?.getBounds?.();
      return bounds?.isValid() ? bounds : null;
    } catch (_) {
      return null;
    }
  }

  /**
   * Re-attaches the target polygon to the map if it was removed externally (e.g. by resetMapNew).
   * Does not re-zoom the map. No-op if targetGeoJson is not set.
   */
  ensureBoundsLayerOnMap(): void {
    if (!this.targetGeoJson) return;
    try {
      if (this.boundsLayer) {
        if (!this.host.map.hasLayer(this.boundsLayer as L.Layer)) {
          (this.boundsLayer as L.Layer).addTo(this.host.map);
        }
      } else {
        this.boundsLayer = L.geoJSON(this.targetGeoJson, {
          style: () => ({
            color: '#1976d2',
            fillColor: 'transparent',
            weight: 2,
            dashArray: '6 4',
          }),
          interactive: false,
        }).addTo(this.host.map);
      }
    } catch (_) {}
  }

  // ── Historical replay ────────────────────────────────────────────────────

  displayHistoricalData(sensor: any, refreshData: any): void {
    const onDone = () => { this.host.spinnerService.hide(); this.host.mapLoad = false; };

    const workspace = refreshData?.geoserverWorkspace;
    if (workspace) {
      this.loadWmsLayers(workspace, [], refreshData.refreshId, onDone, sensor?.collectionMode);
      return;
    }

    const epochDate = `${refreshData?.s3?.key ?? ''}`.split('/')?.[2] ?? '';
    if (!epochDate || !refreshData?.taskid) {
      this.host.spinnerService.hide();
      return;
    }
    this.host.spinnerService.show();
    this.host.mapLoad = true;

    this.host.apiService.getWeatherExHistoricalData({ taskid: refreshData.taskid, date: epochDate })
      .subscribe({
        next: (res: any) => {
          const signedUrl = res?.signedurl;
          if (!signedUrl) { this.host.spinnerService.hide(); this.host.mapLoad = false; return; }
          this.host.httpClient.get(signedUrl).subscribe({
            next: (geoJson: any) => {
              this.renderTimeseries(this.normalizeForRendering(geoJson), refreshData.refreshId, onDone);
            },
            error: () => { this.host.spinnerService.hide(); this.host.mapLoad = false; },
          });
        },
        error: () => { this.host.spinnerService.hide(); this.host.mapLoad = false; },
      });
  }

  // ── Download ─────────────────────────────────────────────────────────────

  downloadHistory(refreshData: any, sensor?: any): void {
    if (this.host.freeze) {
      this.host.popupService.openPopup(CollectionPopupComponent,
        'A download is in progress. Please wait until the current download finishes to continue downloading others.', '', '40%');
      return;
    }

    const epochDate = `${refreshData?.s3?.key ?? ''}`.split('/')?.[2] ?? '';
    if (!epochDate || !refreshData?.taskid) return;

    const taskId  = refreshData.taskid;
    const sensorId = sensor?.id;
    const sceneId  = sensor?.scenes?.[0]?.id;

    this.host.freeze       = true;
    this.host.fail         = 0;
    this.host.progress     = 0;
    this.host.downloadClick = true;
    this.host.spinnerService.show();

    this.host.apiService.getWeatherExHistoricalData({ taskid: taskId, date: epochDate })
      .subscribe({
        next: (res: any) => {
          const signedUrl = res?.signedurl;
          if (!signedUrl) { this.host.resetDownload(); return; }

          const fileName = this.host.getFileNameFromS3SignedUrl(signedUrl);
          this.host.resolveDownloadTarget(fileName).then((downloadTarget) => {
            if (!downloadTarget) { this.host.resetDownload(); return; }

            const xhr = new XMLHttpRequest();
            xhr.open('GET', signedUrl);
            xhr.responseType = 'blob';

            xhr.onprogress = (event) => {
              if (event.lengthComputable) {
                const pct = (event.loaded / event.total) * 100;
                this.host.progress = pct.toFixed(2);
                this.host.downloadService.setProgress({
                  progress: this.host.progress, taskId, sensorId, sceneId,
                  download: this.host.downloadClick, action: 'aisDownload',
                });
              }
            };

            xhr.onload = () => {
              if (xhr.status === 200) {
                this.host.downloadService.setProgress({
                  progress: 100, taskId, sensorId, sceneId,
                  download: this.host.downloadClick, action: 'aisDownload',
                });
                this.host.downloadService.saveBlob(xhr.response, fileName, downloadTarget)
                  .then((saveResult) => {
                    if (saveResult.status === 'cancelled') { this.host.resetDownload(); return; }
                    if (saveResult.status !== 'saved') {
                      this.host.popupService.openPopup(CollectionPopupComponent, saveResult.message, '', '40%');
                      this.host.resetDownload();
                      return;
                    }
                    this.host.progress = 0;
                    this.host.spinnerService.hide();
                    this.host.freeze  = false;
                    this.host.disable = false;
                  });
              }
            };

            xhr.onerror = () => {};
            xhr.send();
          });
        },
        error: () => this.host.resetDownload(),
      });
  }

  // ── Private helpers ──────────────────────────────────────────────────────

  private sortedVarKeys(keys: string[]): string[] {
    const order = this.host.apiService.weatherVariables.map(v => v.key);
    return [...keys].sort((a, b) => {
      const ai = order.indexOf(a);
      const bi = order.indexOf(b);
      return (ai === -1 ? Infinity : ai) - (bi === -1 ? Infinity : bi);
    });
  }

  private sortedCollectionMode(
    cm: { key: string; name: string; description?: string }[] | undefined,
  ): { key: string; name: string; description?: string }[] {
    const order = this.host.apiService.weatherVariables.map(v => v.key);
    return [...(cm ?? [])].sort((a, b) => {
      const ai = order.indexOf(a.key);
      const bi = order.indexOf(b.key);
      return (ai === -1 ? Infinity : ai) - (bi === -1 ? Infinity : bi);
    });
  }

  private uncheckSensor(sensor: any): void {
    const idx = this.host.sensorsNew.findIndex((s: any) => s.id === sensor.id);
    if (idx !== -1) this.host.sensorsNew.splice(idx, 1);
    setTimeout(() => { sensor.checked = false; });
  }

  private applyTimeIndex(index: number): void {
    this.selectedTimeIndex = index;
    const iso = this.times[index] ?? '';
    if (iso) this.updateWmsTime(iso);
  }

  private buildDateIndex(): void {
    this.dates       = [];
    this.timesByDate = {};
    this.times.forEach((iso, index) => {
      const date = `${iso}`.slice(0, 10);
      if (!this.timesByDate[date]) {
        this.timesByDate[date] = [];
        this.dates.push(date);
      }
      this.timesByDate[date].push({ iso, index, label: this.formatTimeLabel(iso) });
    });
    this.dates.sort();
  }

  private formatTimeLabel(iso: string): string {
    const match = /T(\d{2}:\d{2})/.exec(`${iso}`);
    return match ? `${match[1]} UTC` : `${iso}`;
  }

  private createWmsLayer(workspace: string, variable: string, timeIso: string): L.TileLayer {
    const varDef = this.host.apiService.weatherVariables.find(v => v.key === variable);
    const params: any = {
      layers:      `${workspace}:${variable}`,
      styles:      varDef?.wmsStyle ?? `weatherex_${variable}`,
      format:      'image/png',
      transparent:  true,
      TIME:         timeIso,
      zIndex:       900,
    };
    return this.host.helperService.createWmsLayerWithHeader(this.host.map, params);
  }

  private updateWmsTime(timeIso: string): void {
    this.wmsLayers.forEach((wmsLayer, variable) => {
      if (this.activeVariables.has(variable)) {
        (wmsLayer as any).setParams?.({ TIME: timeIso });
      }
    });
    if (this.wmsClickLatLng) {
      this.queryFeatureInfo(this.wmsClickLatLng, timeIso);
    }
  }

  private clearWmsLayers(): void {
    this.wmsLayers.forEach(l => { try { l.remove(); } catch (_) {} });
    this.wmsLayers.clear();
    this.activeVariables.clear();
    this.activeVariablesList = [];
    this.wmsWorkspace        = '';
    this.wmsPointValues      = {};
    this.wmsClickLatLng      = null;
    if (this.clickMarker) {
      try { this.clickMarker.remove(); } catch (_) {}
      this.clickMarker = null;
    }
    this.host.map?.off('click', this.onMapClickBound);
  }

  private onPoiMarkerMouseover(feature: any, marker: any): void {
    try { marker.setStyle(MARKER_HOVER); marker.bringToFront?.(); } catch (_) {}
    this.activeFeature = feature;
    const latlng   = marker.getLatLng();
    const point    = this.host.map.latLngToContainerPoint(latlng);
    const mapSize  = this.host.map.getSize();
    const isAbove  = point.y >= mapSize.y * 0.40;
    this.tooltipPlacement = `${isAbove ? 'above' : 'below'}-${point.x > mapSize.x - 235 ? 'left' : 'right'}`;
    this.tooltipPixelPos  = { x: point.x, y: point.y };
    this.tooltipMaxHeight = Math.max(120, isAbove
      ? point.y - 28
      : mapSize.y - point.y - 28);
    this.tooltipVisible   = true;
    this.host.changDetectionRef.detectChanges();
  }

  private onPoiMarkerMouseout(marker: any): void {
    try { marker.setStyle(MARKER_BASE); } catch (_) {}
    this.host.changDetectionRef.detectChanges();
  }

  private onClickMarkerHover(_e: L.LeafletMouseEvent): void {
    if (!this.wmsClickLatLng || !this.wmsWorkspace) return;
    const point    = this.host.map.latLngToContainerPoint(this.clickMarker!.getLatLng());
    const mapSize  = this.host.map.getSize();
    const isAbove  = point.y >= mapSize.y * 0.40;
    this.tooltipPlacement = `${isAbove ? 'above' : 'below'}-${point.x > mapSize.x - 235 ? 'left' : 'right'}`;
    this.tooltipPixelPos  = { x: point.x, y: point.y };
    this.tooltipMaxHeight = Math.max(120, isAbove
      ? point.y - 28
      : mapSize.y - point.y - 28);
    this.tooltipVisible   = true;

    if (this.activeFeature !== null) {
      this.host.changDetectionRef.detectChanges();
      return;
    }
    this.pointLoading = true;
    const iso = this.times[this.selectedTimeIndex] ?? '';
    this.queryFeatureInfo(this.wmsClickLatLng, iso);
    this.host.changDetectionRef.detectChanges();
  }

  private onMapClick(e: L.LeafletMouseEvent): void {
    if (!this.wmsWorkspace) return;
    if (this.suppressNextMapClick) { this.suppressNextMapClick = false; return; }

    const target = e.originalEvent?.target as HTMLElement;
    if (target?.closest('app-weatherex-point-drawer') || target?.closest('app-weatherex-point-tooltip')) return;

    this.wmsClickLatLng  = e.latlng;
    this.activeFeature   = null;
    this.tooltipVisible  = false;
    this.tooltipPixelPos = null;

    if (this.clickMarker) {
      this.clickMarker.setLatLng(e.latlng);
    } else {
      this.clickMarker = L.circleMarker(e.latlng, {
        radius: 8, color: '#ffffff', weight: 2,
        fillColor: '#4fc3f7', fillOpacity: 0.85, pane: 'markerPane',
      }).addTo(this.host.map);
      this.clickMarker.on('mouseover', this.onClickMarkerHoverBound);
    }
  }

  private queryFeatureInfo(latlng: L.LatLng, timeIso: string): void {
    const allVars = this.variables.length ? this.variables : Array.from(this.activeVariables);
    if (!allVars.length || !this.wmsWorkspace) return;

    const layerNames = allVars.map(v => `${this.wmsWorkspace}:${v}`).join(',');
    const mapSize    = this.host.map.getSize();
    const bounds     = this.host.map.getBounds();
    const point      = this.host.map.latLngToContainerPoint(latlng);

    const params = new URLSearchParams({
      SERVICE: 'WMS', VERSION: '1.1.1', REQUEST: 'GetFeatureInfo',
      LAYERS: layerNames, QUERY_LAYERS: layerNames, TIME: timeIso,
      INFO_FORMAT: 'application/json', FEATURE_COUNT: String(allVars.length),
      X: String(Math.round(point.x)), Y: String(Math.round(point.y)),
      WIDTH: String(mapSize.x), HEIGHT: String(mapSize.y),
      BBOX: `${bounds.getWest()},${bounds.getSouth()},${bounds.getEast()},${bounds.getNorth()}`,
      SRS: 'EPSG:4326',
    });

    const storedJwt = (sessionStorage.getItem('jwttoken') || '').replace(/^"|"$/g, '');
    this.host.http.get<any>(
      `${environment.geoServerUrl}?${params.toString()}`,
      { headers: { Authorization: `Bearer ${storedJwt}` } },
    ).subscribe({
      next: (resp) => {
        if (!resp?.features?.length) {
          this.closeTooltip();
          this.host.popupService.openPopup(
            CollectionPopupComponent,
            'No forecast data at this location. Click within the data coverage area.',
            '',
            '40%',
          );
          return;
        }
        const values: { [v: string]: number | null } = {};
        (resp?.features ?? []).forEach((f: any) => {
          const props = f?.properties ?? {};
          for (const [key, rawVal] of Object.entries(props)) {
            if (key === 'GRAY_INDEX') {
              const rawId: string = f?.id ?? '';
              const varName = rawId.split(':').pop()?.replace(/\.\d+$/, '') ?? '';
              if (varName) values[varName] = typeof rawVal === 'number' ? rawVal : null;
            } else {
              const num = typeof rawVal === 'number' ? rawVal : Number(rawVal);
              values[key] = Number.isFinite(num) ? num : null;
            }
          }
        });
        this.wmsPointValues = values;
        this.wmsPointValues['_lat'] = latlng.lat as any;
        this.wmsPointValues['_lng'] = latlng.lng as any;

        const syntheticProps: any = { latitude: latlng.lat, longitude: latlng.lng };
        for (const [varName, val] of Object.entries(values)) syntheticProps[varName] = val;
        this.activeFeature = { type: 'Feature', properties: syntheticProps };
        this.pointLoading = false;
        this.host.changDetectionRef.detectChanges();
      },
      error: () => {
        this.closeTooltip();
        this.host.changDetectionRef.detectChanges();
      },
    });
  }

  // ── GeoJSON normalisation (POI flat format → Wide format) ─────────────────
  //
  // POI data from the backend is a flat FeatureCollection: one feature per
  // timestamp, each with scalar properties and a `timestamp` key.
  // reshapeWfsToWide groups them into one feature per grid-point with
  // per-variable time-indexed arrays — the Wide format renderTimeseries() uses.
  //
  // Case 1 (root times[] present) is a safety guard for any future pre-processed
  // wide-format GeoJSON; it is not produced by the current backend.

  normalizeForRendering(geoJson: any): any {
    // Already in Wide format — pass through.
    if (Array.isArray(geoJson?.times) && geoJson.times.length > 0) return geoJson;
    // Flat format: one feature per timestamp (POI response).
    return this.reshapeWfsToWide(geoJson);
  }

  private reshapeWfsToWide(flatFc: any): any {
    const features: any[] = flatFc?.features ?? [];
    const META_KEYS = new Set(['timestamp', 'grid_i', 'grid_j', 'latitude', 'longitude', 'fid', 'id', 'the_geom']);

    const timesSet = new Set<string>();
    for (const f of features) { const ts = f?.properties?.timestamp; if (ts) timesSet.add(ts); }
    const times     = Array.from(timesSet).sort();
    const timeIndex = new Map(times.map((t, i) => [t, i]));

    const sampleProps = features[0]?.properties ?? {};
    const variables   = Object.keys(sampleProps).filter(k => !META_KEYS.has(k));

    const byCoord = new Map<string, { feature: any; vals: { [v: string]: (number | null)[] } }>();
    for (const f of features) {
      const coords = f?.geometry?.coordinates;
      if (!coords) continue;
      const key  = `${coords[0]},${coords[1]}`;
      const ts: string = f?.properties?.timestamp;
      const tIdx = timeIndex.get(ts);
      if (tIdx == null) continue;
      if (!byCoord.has(key)) {
        const vals: { [v: string]: (number | null)[] } = {};
        for (const v of variables) vals[v] = new Array(times.length).fill(null);
        byCoord.set(key, { feature: f, vals });
      }
      const entry = byCoord.get(key)!;
      for (const v of variables) {
        const val = f?.properties?.[v];
        if (val != null) entry.vals[v][tIdx] = val;
      }
    }

    const wideFeatures = Array.from(byCoord.values()).map(({ feature, vals }) => ({
      type: 'Feature',
      geometry: feature.geometry,
      properties: {
        ...vals,
        latitude:  feature?.properties?.latitude  ?? feature?.geometry?.coordinates?.[1],
        longitude: feature?.properties?.longitude ?? feature?.geometry?.coordinates?.[0],
      },
    }));
    return { type: 'FeatureCollection', times, variables, units: {}, features: wideFeatures };
  }
}
